import React from 'react';
import ReactDOM from 'react-dom';
import { useFieldArray, useForm, FormProvider, useFormContext, Controller } from 'react-hook-form';
import { Common } from '../../utils/common';
import { QueryClient, QueryClientProvider, useQuery, useMutation } from '@tanstack/react-query';
import Select from 'react-select';
import { Permissions } from '../../utils/permissions';

const queryClient = new QueryClient();

class DefaultSettings extends HTMLElement {
  constructor() {
    super();
  }

  async connectedCallback() {
    this.innerHTML = `
      <style>
        .divide-y > * + * {
          border-top-width: 1px;
          padding-top: 25px;
          border-color: #ccc;
          border-top-style: solid;
        }
      </style>

      <div id="default-settings-root"></div>
    `;

    this.group_id = document.querySelector('#object_id').value;

    const isAdmin = await Permissions.hasPermissionForGroup(
      Permissions.ALL.admin_is_admin,
      this.group_id
    );

    ReactDOM.render(
      <QueryClientProvider client={queryClient}>
        <DefaultSettingsReact groupId={this.group_id} isAdmin={isAdmin} />
      </QueryClientProvider>,
      this.querySelector('#default-settings-root')
    );
  }
}

customElements.define('default-settings', DefaultSettings);

/**
 * @param {DefaultAction[]} defaultActions
 * @returns {Webhook[]}
 */
function parseDefaultActions(defaultActions) {
  return defaultActions.map((defaultAction) => {
    /** @type {Webhook} */
    const webhook = {
      id: defaultAction.id.toString(),
      webhookName: defaultAction.name,
      template: defaultAction.action_template_id.toString(),
      url: '',
      successCode: '200',
      httpMethod: 'POST',
      headers: [],
    };

    defaultAction.default_action_parameters.forEach((parameter) => {
      switch (parameter.parameter_name) {
        case 'url':
          webhook.url = parameter.parameter_value;
          break;
        case 'success_code':
          webhook.successCode = parameter.parameter_value;
          break;
        case 'http_method':
          webhook.httpMethod = parameter.parameter_value;
          break;
        case 'headers': {
          const headers = JSON.parse(parameter.parameter_value);
          Object.keys(headers).forEach((header) => {
            webhook.headers.push({ header, value: headers[header] });
          });
          break;
        }
      }
    });

    return webhook;
  });
}

/** @type {Webhook} */
const defaultFormValues = {
  id: '',
  webhookName: '',
  url: '',
  template: '',
  successCode: '200',
  httpMethod: 'POST',
  headers: [{ header: '', value: '' }],
};

/**
 * @param {{ groupId: string; isAdmin: boolean; }} props
 */
function DefaultSettingsReact({ groupId, isAdmin }) {
  const [actionTemplates, setActionTemplates] = React.useState(
    /** @type {ActionTemplate[] | undefined} */ (undefined)
  );

  React.useEffect(() => {
    fetch(`/admin/auto_group_defaults/action_templates/${groupId}`)
      .then((response) => response.json())
      .then((data) => {
        setActionTemplates(data);
      });
  }, [groupId]);

  const { data: defaultWebhooks } = useQuery({
    queryKey: ['defaultActions', groupId],
    queryFn: async () => {
      const res = await fetch(`/admin/default_actions/${groupId}`);
      if (!res.ok) {
        throw new Error('Failed to fetch default actions');
      }
      /** @type {{ default_actions: DefaultAction[], default_enablement: AutoGroupDefaultValue }} */
      const data = await res.json();
      return {
        defaultActions: parseDefaultActions(data.default_actions),
        defaultEnablement: data.default_enablement === null ? 'inherit' : data.default_enablement,
      };
    },
    staleTime: Infinity,
  });

  const {
    data: inheritedDefaults,
    isError,
    error,
  } = useQuery({
    queryKey: ['inheritedDefaults', groupId],
    queryFn: async () => {
      const res = await fetch(`/admin/auto_group_defaults/inherited_defaults/${groupId}`);
      if (!res.ok) {
        const { error } = await res.json();
        throw new Error(error);
      }
      /** @type {InheritedDefaults} */
      const data = await res.json();
      return data;
    },
    staleTime: Infinity,
    retry: false,
  });

  const { data: autoGroupDefaults } = useQuery({
    queryKey: ['autoGroupDefaults', groupId],
    queryFn: async () => {
      const res = await fetch(`/admin/auto_group_defaults/${groupId}`);
      if (!res.ok) {
        throw new Error('Failed to fetch auto group defaults');
      }
      /** @type {AutoGroupDefault[]} */
      const data = await res.json();
      return data;
    },
    staleTime: Infinity,
  });

  const { data: tagGroupOptions } = useQuery({
    queryKey: ['tagGroupOptions', groupId],
    queryFn: async () => {
      const res = await fetch(`/admin/groups/${groupId}/tag_groups`);
      if (!res.ok) {
        throw new Error('Failed to fetch tag groups');
      }
      /** @type {{ data: TagGroupOption[] }} */
      const { data } = await res.json();
      return data;
    },
    staleTime: Infinity,
  });

  const { data: requiredTagGroups } = useQuery({
    queryKey: ['requiredTagGroups', groupId],
    queryFn: async () => {
      const res = await fetch(`/admin/default_tag_groups/${groupId}`);
      if (!res.ok) {
        throw new Error('Failed to fetch required tag groups');
      }
      /** @type {RequiredTagGroup[]} */
      const data = await res.json();
      return data;
    },
    staleTime: Infinity,
  });

  if (isError) {
    return (
      <div className='container-fluid'>
        <div className='alert alert-warning' role='alert'>
          {error.message}
        </div>
      </div>
    );
  }

  if (
    defaultWebhooks &&
    actionTemplates &&
    inheritedDefaults &&
    autoGroupDefaults &&
    tagGroupOptions &&
    requiredTagGroups
  ) {
    return (
      <div className='container-fluid'>
        <div className='panel-group' id='default-settings-accordion' role='tablist'>
          <WebhookPanel
            defaultWebhooks={defaultWebhooks.defaultActions}
            defaultEnablement={defaultWebhooks.defaultEnablement}
            actionTemplates={actionTemplates}
            groupId={groupId}
            inheritedDefaults={inheritedDefaults}
          />
          <CampaignDefaults
            inheritedDefaults={inheritedDefaults}
            groupId={groupId}
            autoGroupDefaults={autoGroupDefaults}
            tagGroupOptions={tagGroupOptions}
            requiredTagGroups={requiredTagGroups}
            isAdmin={isAdmin}
          />
        </div>
      </div>
    );
  } else {
    return null;
  }
}

/**
 * @param {string} targetId - The id of the target element
 * @returns {boolean} Boolean indicating whether the panel is open or not
 */
function usePanelIsOpen(targetId) {
  const [panelIsOpen, setPanelIsOpen] = React.useState(false);

  React.useEffect(() => {
    $(targetId).on('show.bs.collapse', () => {
      setPanelIsOpen(true);
    });
    $(targetId).on('hide.bs.collapse', () => {
      setPanelIsOpen(false);
    });
  }, [targetId]);

  return panelIsOpen;
}

/**
 * @param {{
 *   defaultWebhooks: Webhook[];
 *   actionTemplates: ActionTemplate[];
 *   groupId: string;
 *   defaultEnablement: AutoGroupDefaultValue;
 *   inheritedDefaults: InheritedDefaults;
 * }} props
 */
function WebhookPanel({
  defaultWebhooks,
  actionTemplates,
  groupId,
  defaultEnablement,
  inheritedDefaults,
}) {
  /** @type {FormData} */
  const defaultValues = React.useMemo(
    () => ({ defaultEnablement, webhooks: defaultWebhooks }),
    [defaultWebhooks, defaultEnablement]
  );

  const methods = useForm({ defaultValues });

  React.useEffect(() => {
    // reset the form state to the server values after saving
    if (methods.formState.isSubmitSuccessful) {
      methods.reset(defaultValues);
    }
  }, [defaultValues, methods]);

  const saveWebhooksMutation = useMutation({
    mutationFn: saveWebhooks,
    onSuccess: async () => {
      const invalidateQueriesPromises = [
        queryClient.invalidateQueries({ queryKey: ['defaultActions', groupId] }),
        queryClient.invalidateQueries({ queryKey: ['inheritedDefaults', groupId] }),
        queryClient.invalidateQueries({ queryKey: ['autoGroupDefaults', groupId] }),
      ];

      await Promise.all(invalidateQueriesPromises);
      Common.notify('Webhooks saved successfully', 'success');
    },
    onError: () => {
      Common.notify('Failed to save webhooks', 'error');
    },
  });

  const defaultEnablementValue = methods.watch('defaultEnablement');

  const webhooksAreDisabled =
    defaultEnablementValue === '0' ||
    (defaultEnablementValue === 'inherit' && inheritedDefaults.actions === '0');

  React.useEffect(() => {
    if (webhooksAreDisabled) {
      methods.setValue('webhooks', []);
    } else {
      methods.setValue('webhooks', defaultWebhooks);
    }
  }, [webhooksAreDisabled, defaultWebhooks, methods]);

  /**
   * @param {FormData} data
   */
  async function saveWebhooks(data) {
    /**
     * @type {{
     *   id: string;
     *   webhookName: string;
     *   url: string;
     *   template: string;
     *   successCode: string;
     *   httpMethod: string;
     *   headers: Record<string, string>;
     * }[]}
     */
    const webhooks = data.webhooks.map((webhook) => ({
      id: webhook.id,
      webhookName: webhook.webhookName,
      url: webhook.url,
      template: webhook.template,
      successCode: webhook.successCode,
      httpMethod: webhook.httpMethod,
      headers: webhook.headers.reduce((acc, curr) => {
        acc[curr.header] = curr.value;
        return acc;
      }, {}),
    }));

    const res = await fetch(`/admin/auto_group_defaults/${groupId}/location_defaults`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name=csrf-token]').content,
      },
      body: JSON.stringify({ webhooks, default_enablement: data.defaultEnablement }),
    });

    if (res.ok) {
      /** @type {DefaultAction[]} */
      const data = await res.json();
      return parseDefaultActions(data);
    } else {
      throw new Error('Failed to save webhooks');
    }
  }

  const { fields, append, remove } = useFieldArray({ control: methods.control, name: 'webhooks' });

  const panelIsOpen = usePanelIsOpen('#default-webhooks');

  return (
    <div className='panel panel-default'>
      <a
        data-toggle='collapse'
        data-parent='#default-settings-accordion'
        href='#default-webhooks'
        aria-controls='default-webhooks'
        className='panel-heading'
        role='tab'
        id='default-webhooks-heading'
      >
        <h4 className='panel-title'>
          {panelIsOpen ? (
            <i className='fa fa-chevron-down' />
          ) : (
            <i className='fa fa-chevron-right' />
          )}
          <span style={{ marginLeft: 10 }}>Location Default Webhooks</span>
        </h4>
      </a>
      <div
        id='default-webhooks'
        className='panel-collapse collapse'
        role='tabpanel'
        aria-labelledby='default-webhooks-heading'
      >
        <form
          onSubmit={methods.handleSubmit((data) => saveWebhooksMutation.mutate(data))}
          className='panel-body'
        >
          <div
            style={{
              paddingLeft: 10,
              paddingRight: 10,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: 10,
            }}
          >
            <div className='form-group'>
              <label htmlFor='defaultEnablement' className='control-label'>
                Webhook Default Enablement
              </label>
              <select
                className='form-control'
                id='defaultEnablement'
                {...methods.register('defaultEnablement')}
              >
                <option value='inherit'>Inherit</option>
                <option value='1'>On</option>
                <option value='0'>Off</option>
              </select>
            </div>
            {webhooksAreDisabled ? null : (
              <button
                className='btn btn-link pull-right'
                type='button'
                disabled={fields.length >= 5}
                onClick={() => append(defaultFormValues)}
              >
                Add Webhook
              </button>
            )}
          </div>
          {webhooksAreDisabled ? (
            <span style={{ paddingLeft: 10 }} className='help-block'>
              Any existing default webhooks will be removed
            </span>
          ) : (
            <FormProvider {...methods}>
              <div className='divide-y'>
                {fields.map((field, index) => (
                  <WebhookForm
                    key={field.id}
                    removeWebhook={() => remove(index)}
                    index={index}
                    actionTemplates={actionTemplates}
                  />
                ))}
              </div>
            </FormProvider>
          )}
          <div className='row'>
            <button
              disabled={saveWebhooksMutation.isLoading}
              type='submit'
              className='btn pull-right btn-primary'
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * @param {{
 *  index: number;
 *  removeWebhook: () => void;
 *  actionTemplates: ActionTemplate[];
 * }} props
 */
function WebhookForm({ index, removeWebhook, actionTemplates }) {
  const { control, register, formState } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name: `webhooks.${index}.headers` });

  function addHttpHeader() {
    if (fields.length < 5) {
      append({ header: '', value: '' });
    }
  }

  /**
   * @param {number} index
   */
  function removeHttpHeader(index) {
    if (fields.length > 1) {
      remove(index);
    }
  }

  /**
   * @param {keyof FormData['webhooks'][number]} fieldName
   */
  function fieldHasError(fieldName) {
    return formState.errors?.webhooks?.[index]?.[fieldName] !== undefined;
  }

  const errorMessage = <span className='help-block'>This field is required.</span>;

  return (
    <div style={{ marginBottom: 10 }}>
      <input type='hidden' {...register(`webhooks.${index}.id`)} />
      <div className='row' style={{ marginBottom: 15 }}>
        <div className='col-sm-6'>
          <div className={`form-group ${fieldHasError('webhookName') ? 'has-error' : ''}`}>
            <label className='control-label' htmlFor='webhookName'>
              Webhook Name
            </label>
            <input
              type='text'
              id='webhookName'
              className='form-control'
              {...register(`webhooks.${index}.webhookName`, { required: true })}
            />
            {fieldHasError('webhookName') ? errorMessage : null}
          </div>
        </div>
        <div className='col-sm-6'>
          <div className={`form-group ${fieldHasError('url') ? 'has-error' : ''}`}>
            <label className='control-label' htmlFor='url'>
              URL
            </label>
            <input
              type='text'
              className='form-control'
              placeholder='example: http://mydomain.com/?param=mystaticparam'
              id='url'
              {...register(`webhooks.${index}.url`, { required: true })}
            />
            {fieldHasError('url') ? errorMessage : null}
          </div>
        </div>
      </div>
      <div className='row' style={{ marginBottom: 15 }}>
        <div className='col-sm-6'>
          <div className={`form-group ${fieldHasError('template') ? 'has-error' : ''}`}>
            <label className='control-label' htmlFor='template'>
              Template
            </label>
            <select
              id='template'
              className='form-control template'
              {...register(`webhooks.${index}.template`, { required: true })}
            >
              <option value=''>Select Template</option>
              {actionTemplates.map((template) => (
                <option key={template.action_template_id} value={template.action_template_id}>
                  {template.name}
                </option>
              ))}
            </select>
            {fieldHasError('template') ? errorMessage : null}
          </div>
        </div>
        <div className='col-sm-6'>
          <div className={`form-group ${fieldHasError('successCode') ? 'has-error' : ''}`}>
            <label className='control-label' htmlFor='successCode'>
              Success Response Code
            </label>
            <select
              id='successCode'
              className='form-control'
              {...register(`webhooks.${index}.successCode`, { required: true })}
            >
              <option>200</option>
              <option>201</option>
              <option>202</option>
              <option>203</option>
              <option>204</option>
            </select>
            {fieldHasError('successCode') ? errorMessage : null}
          </div>
        </div>
      </div>
      <div className='row'>
        <div className='col-sm-6' style={{ marginBottom: 15 }}>
          <div className={`form-group ${fieldHasError('httpMethod') ? 'has-error' : ''}`}>
            <label className='control-label'>HTTP Method</label>
            <select
              className='form-control'
              {...register(`webhooks.${index}.httpMethod`, { required: true })}
            >
              <option value='POST'>POST</option>
              <option value='PUT'>PUT</option>
            </select>
            {fieldHasError('httpMethod') ? errorMessage : null}
          </div>
        </div>
        <div className='col-sm-12 col-lg-6' style={{ marginBottom: 15 }}>
          <div className='form-group'>
            <div style={{ marginRight: -10, marginLeft: -10 }} className='row'>
              <div className='col-sm-5'>
                <label className='control-label'>HTTP Header</label>
              </div>
              <div className='col-sm-5'>
                <label className='control-label'>Value</label>
              </div>
            </div>
            <div>
              {fields.map((item, idx) => (
                <div style={{ marginRight: -10, marginLeft: -10 }} className='row' key={item.id}>
                  <div
                    className={`col-sm-5 ${
                      formState.errors?.webhooks?.[index]?.headers?.[idx]?.header ? 'has-error' : ''
                    }`}
                  >
                    <input
                      type='text'
                      className='form-control'
                      placeholder='Content-Type'
                      {...register(`webhooks.${index}.headers.${idx}.header`, { required: true })}
                    />
                    {formState.errors?.webhooks?.[index]?.headers?.[idx]?.header
                      ? errorMessage
                      : null}
                  </div>
                  <div
                    className={`col-sm-5 ${
                      formState.errors?.webhooks?.[index]?.headers?.[idx]?.value ? 'has-error' : ''
                    }`}
                  >
                    <input
                      type='text'
                      className='form-control'
                      placeholder='application/json'
                      {...register(`webhooks.${index}.headers.${idx}.value`, { required: true })}
                    />
                    {formState.errors?.webhooks?.[index]?.headers?.[idx]?.value
                      ? errorMessage
                      : null}
                  </div>
                  <div className='col-sm-2' style={{ lineHeight: '30px' }}>
                    <a onClick={() => removeHttpHeader(idx)}>
                      <span className='fa fa-minus'></span>
                    </a>
                    <a style={{ marginLeft: 5 }} onClick={addHttpHeader}>
                      <span className='fa fa-plus'></span>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type='button' className='btn btn-sm btn-danger' onClick={removeWebhook}>
          Remove Webhook
        </button>
      </div>
    </div>
  );
}

const campaignSettingLabels = /** @type {const} */ ([
  { name: 'call_disclaimer', label: 'Call Disclaimer Language' },
  { name: 'call_recording', label: 'Enable Call Recording' },
  { name: 'leadscore', label: 'Enable Leadscore' },
  { name: 'redaction', label: 'Enable Call Redaction' },
  { name: 'transcription', label: 'Enable Call Transcription' },
  { name: 'tag_groups', label: 'Require Tag Groups' },
]);

/**
 * @typedef {{
 *   [key in typeof campaignSettingLabels[number]['name']]: string;
 * }} CampaignSettings
 */

/**
 * @typedef {{
 *   campaign_settings: CampaignSettings;
 *   required_tag_groups: { label: string; value: number; }[];
 * }} CampaignDefaultsFormData
 */

/**
 * @param {{
 *   inheritedDefaults: InheritedDefaults;
 *   groupId: string;
 *   autoGroupDefaults: AutoGroupDefault[];
 *   tagGroupOptions: TagGroupOption[];
 *   requiredTagGroups: RequiredTagGroup[];
 *   isAdmin: boolean;
 * }} props
 */
function CampaignDefaults({
  inheritedDefaults,
  groupId,
  autoGroupDefaults,
  tagGroupOptions,
  requiredTagGroups,
  isAdmin,
}) {
  const panelIsOpen = usePanelIsOpen('#campaign-settings');

  /** @type {CampaignDefaultsFormData} */
  const defaultValues = React.useMemo(() => {
    const campaign_settings = campaignSettingLabels.reduce((formValues, campaignDefault) => {
      const initialDefault = autoGroupDefaults.find((item) => item.code === campaignDefault.name);
      formValues[campaignDefault.name] = initialDefault?.value ?? 'inherit';
      return formValues;
    }, /** @type {CampaignSettings} */ ({}));

    // In order to have the correct tag group option selected, we need the entire { label, value }
    // object, not just the id. So we need to find the tagGroupOptions that match the requiredTagGroups
    // to create a { label, value } object with the name and id
    const initialTagGroupOptions = /** @type {TagGroupOption[]} */ (
      requiredTagGroups.map((requiredTagGroup) => {
        return tagGroupOptions.find((tagGroupOption) => {
          return tagGroupOption.tag_group_id === requiredTagGroup.tag_group_id;
        });
      })
    );

    const required_tag_groups = initialTagGroupOptions.map((tagGroupOption) => {
      return {
        label: tagGroupOption.name,
        value: tagGroupOption.tag_group_id,
      };
    });

    return { campaign_settings, required_tag_groups };
  }, [autoGroupDefaults, requiredTagGroups, tagGroupOptions]);

  const campaignDefaultsMutation = useMutation({
    mutationFn: (/** @type {CampaignDefaultsFormData} */ data) => {
      const tagGroupIds = data.required_tag_groups.map((tagGroup) => tagGroup.value);

      return fetch(`/admin/auto_group_defaults/${groupId}/campaign_defaults`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name=csrf-token]').content,
        },
        body: JSON.stringify({
          campaign_defaults: data.campaign_settings,
          required_tag_groups: tagGroupIds,
        }),
      });
    },
    onSuccess: async () => {
      const invalidateQueriesPromises = [
        queryClient.invalidateQueries({ queryKey: ['inheritedDefaults', groupId] }),
        queryClient.invalidateQueries({ queryKey: ['requiredTagGroups', groupId] }),
        queryClient.invalidateQueries({ queryKey: ['autoGroupDefaults', groupId] }),
      ];

      await Promise.all(invalidateQueriesPromises);
      Common.notify('Campaign defaults saved successfully', 'success');
    },
    onError: () => {
      Common.notify('Failed to save campaign defaults', 'error');
    },
  });

  const { register, handleSubmit, reset, watch, formState, control, setValue } = useForm({
    defaultValues,
  });

  React.useEffect(() => {
    // reset the form state to the server values after saving
    if (campaignDefaultsMutation.isSuccess) {
      reset(defaultValues);
    }
  }, [defaultValues, campaignDefaultsMutation.isSuccess, reset]);

  const currentValues = watch();

  const formattedTagGroups = tagGroupOptions.map((tagGroup) => {
    return {
      label: tagGroup.name,
      value: tagGroup.tag_group_id,
    };
  });

  const shouldShowTagGroups = currentValues.campaign_settings.tag_groups === '1';

  const selectedTagGroups = watch('required_tag_groups');

  const tagGroupsIsDisabled =
    !isAdmin && defaultValues.campaign_settings.tag_groups === '1' && requiredTagGroups.length > 0;

  // If the tag groups are hidden, the value of the form data should be cleared as well
  // to send the correct data to the server
  React.useEffect(() => {
    if (shouldShowTagGroups) {
      setValue('required_tag_groups', defaultValues.required_tag_groups);
    } else {
      setValue('required_tag_groups', []);
    }
  }, [defaultValues.required_tag_groups, setValue, shouldShowTagGroups]);

  return (
    <div className='panel panel-default'>
      <a
        data-toggle='collapse'
        data-parent='#default-settings-accordion'
        href='#campaign-settings'
        aria-controls='campaign-settings'
        className='panel-heading'
        role='tab'
        id='campaign-settings-heading'
      >
        <h4 className='panel-title'>
          {panelIsOpen ? (
            <i className='fa fa-chevron-down' />
          ) : (
            <i className='fa fa-chevron-right' />
          )}
          <span style={{ marginLeft: 10 }}>Campaign Settings</span>
        </h4>
      </a>
      <div
        id='campaign-settings'
        className='panel-collapse collapse'
        role='tabpanel'
        aria-labelledby='campaign-settings-heading'
      >
        <form
          onSubmit={handleSubmit((data) => campaignDefaultsMutation.mutate(data))}
          className='panel-body'
        >
          {campaignSettingLabels.map((item) => {
            const isInherit = currentValues.campaign_settings[item.name] === 'inherit';
            const inheritedValue = inheritedDefaults[item.name];

            const isDisabled = tagGroupsIsDisabled && item.name === 'tag_groups';

            return (
              <div style={{ marginBottom: 15 }} className='row' key={item.name}>
                <label style={{ marginBottom: 0, paddingTop: 7 }} className='col-sm-5'>
                  {item.label}
                </label>
                <div className='col-sm-7'>
                  {item.name === 'call_disclaimer' ? (
                    <select
                      className='form-control'
                      {...register('campaign_settings.call_disclaimer')}
                    >
                      <option value='inherit'>Inherit</option>
                      <option value='1'>English</option>
                      <option value='2'>Spanish</option>
                      <option value='3'>French</option>
                    </select>
                  ) : (
                    <select
                      className='form-control'
                      {...register(`campaign_settings.${item.name}`)}
                      disabled={isDisabled}
                    >
                      <option value='inherit'>Inherit</option>
                      <option value='1'>On</option>
                      <option value='0'>Off</option>
                    </select>
                  )}
                </div>
                {isInherit ? (
                  // Tag groups should not show the inherited value
                  item.name === 'tag_groups' ? null : item.name === 'call_disclaimer' ? (
                    <span className='help-block col-xs-12'>
                      Inheritted{' '}
                      {inheritedValue === '0'
                        ? 'None'
                        : inheritedValue === '1'
                        ? 'English'
                        : inheritedValue === '2'
                        ? 'Spanish'
                        : 'French'}
                    </span>
                  ) : (
                    <span className='help-block col-xs-12'>
                      Inheritted {inheritedValue === '0' ? 'Off' : 'On'}
                    </span>
                  )
                ) : null}
              </div>
            );
          })}
          {shouldShowTagGroups ? (
            <div className='row' style={{ marginBottom: 15 }}>
              <div
                className={`col-xs-12 ${formState.errors.required_tag_groups ? 'has-error' : ''}`}
              >
                <label htmlFor='required_tag_groups' className='control-label'>
                  Required Tag Groups
                </label>
                <Controller
                  name='required_tag_groups'
                  control={control}
                  rules={{
                    // At least one required tag group should be selected when tag groups is turned on
                    validate: (value, values) => {
                      return values.campaign_settings.tag_groups === '1' && value?.length > 0;
                    },
                  }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      id='required_tag_groups'
                      options={formattedTagGroups}
                      isMulti={true}
                      isOptionDisabled={() => selectedTagGroups.length > 4}
                      isDisabled={tagGroupsIsDisabled}
                    />
                  )}
                />
                {formState.errors.required_tag_groups ? (
                  <span className='help-block'>Select at least one required tag group.</span>
                ) : null}
              </div>
            </div>
          ) : null}
          <div className='row'>
            <button
              disabled={campaignDefaultsMutation.isLoading}
              type='submit'
              className='btn pull-right btn-primary'
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * @typedef {{
 *   id: number;
 *   auto_groupId: number;
 *   name: string;
 *   action_type: 'WEBHOOK';
 *   action_template_id: number;
 *   default_action_parameters: {
 *     default_action_id: number;
 *     id: number;
 *     parameter_name: string;
 *     parameter_value: string;
 *   }[];
 * }} DefaultAction
 */

/**
 * @typedef {{
 *   id: string;
 *   webhookName: string;
 *   url: string;
 *   template: string;
 *   successCode: string;
 *   httpMethod: string;
 *   headers: { header: string; value: string }[];
 * }} Webhook
 */

/**
 * @typedef {{
 *  action_template_id: number;
 *  name: string;
 *  template_type: string;
 *  auto_group_name: string;
 * }} ActionTemplate
 */

/**
 * @typedef {{
 *   webhooks: Webhook[];
 *   defaultEnablement: AutoGroupDefaultValue;
 * }} FormData
 */

/**
 * @typedef {{
 *   auto_group_id: number;
 *   call_recording: string;
 *   transcription: string;
 *   redaction: string;
 *   call_disclaimer: string;
 *   ls_duration: string;
 *   chat: string;
 *   book_now: string;
 *   processing_queue: string | null;
 *   leadscore: string;
 *   dealsaver: string;
 *   actions: string;
 *   proc_actions: string;
 * }} InheritedDefaults
 */

/**
 * @typedef {{
 *   id: number;
 *   auto_group_id: number;
 *   code: string;
 *   value: string;
 * }} AutoGroupDefault
 */

/**
 * @typedef {{
 *   tag_group_id: number;
 *   auto_group_id: number;
 *   name: string;
 *   ordinal: number;
 *   is_used: boolean;
 * }} TagGroupOption
 */

/**
 * @typedef {{
 *   id: number;
 *   auto_group_id: number;
 *   tag_group_id: number;
 * }} RequiredTagGroup
 */

/**
 * @typedef {'0' | '1' | 'inherit'} AutoGroupDefaultValue
 */
